import { LINK_DOMAIN } from "@/lib/utils/navigation-utils";
import {
  buildRedirectAttemptCookie,
  hasRedirectBeenAttempted,
  markRedirectAttempted,
} from "@/lib/utils/redirectFlow/redirectAttempt";
import { BrokenInAppBrowser } from "./detect";
import { EscapeAttempt, xSafariAttempt } from "./escapeAttempts";
import {
  buildSpinnerDocument,
  escapeHtml,
  toScriptJson,
} from "./escapeDocument";

export const APP_BOUNCE_PATH = "/api/link/bounce";

// "www" can never be an organization's subdomain, so this host differs from
// every link host.
const APP_BOUNCE_ORIGIN = `https://www.${LINK_DOMAIN}`;

// Kept on the link's host for 30 s rather than in the link, which the app
// would get. Holds the page that bounced, so another link opened in those 30 s
// still bounces.
const BOUNCED_KEY = "detour_bounced";

// The token copy-link put on the clipboard before a bounce. The page the bounce
// comes back to records its click with it, so the app can still match the
// clipboard after the install.
const BOUNCED_PASTEBOARD_TOKEN_KEY = "detour_bounced_pasteboard_token";
const PASTEBOARD_TOKEN_PATTERN = /^[A-Za-z0-9_-]{22}$/;

// Messenger sends Facebook's user agent. FBOP/80 is the only token that
// differs (FBOP/5 in Facebook).
const MESSENGER_UA_PATTERN = /fbop\/80\b/i;

/**
 * Workaround for Facebook on iOS. Its webview never opens the app from a
 * Universal Link by itself: links from posts reach the page through an HTTP
 * redirect (lm.facebook.com), and iOS only checks for a Universal Link when a
 * navigation starts. A new navigation to the link from another host does open
 * the app, so the page leaves through www on the link domain, which goes
 * straight on to the link.
 *
 * Tested on a device on 2026-10-15 (iPhone 11, iOS 26.5.2, Facebook 582.0.0):
 * - x-safari to that page with no tap: Facebook's prompt, Safari, then the
 *   app. Without the app Safari loads the link page, whose normal flow opens
 *   the App Store.
 * - A tap that goes to that page inside Facebook: the app. Without it the link
 *   page loads again, and its store redirect opens the App Store sheet,
 *   because the tap still counts.
 * - The same with no tap, inside Facebook: the link page just loads.
 * - Profile (bio) links: x-safari does nothing, see isFacebookPostLink.
 *
 * Not Messenger: it hands links to iOS itself, so the app already opens.
 */
export function bouncesToApp(
  browser: BrokenInAppBrowser,
  userAgent: string
): boolean {
  return browser === "facebook" && !MESSENGER_UA_PATTERN.test(userAgent);
}

/**
 * Whether Facebook opened this page from a post or an ad, which go through its
 * link redirect (lm.facebook.com) and get an fbclid. Profile (bio) links skip
 * it and get none. There x-safari does nothing, and afterwards Facebook
 * ignores every navigation a script starts in that tab, even on the next page
 * and even right after a tap. Only plain links still work.
 *
 * Tested on a device on 2026-10-15 (iPhone 11, iOS 26.5.2, Facebook 582.0.0):
 * every post link had an fbclid, no bio link did.
 */
export function isFacebookPostLink(pageUrl: string): boolean {
  return new URL(pageUrl).searchParams.has("fbclid");
}

/** The page that sends the visitor on to this page's link. */
export function buildAppBounceUrl(pageUrl: string): string {
  const linkUrl = new URL(pageUrl);
  linkUrl.hash = "";
  return `${APP_BOUNCE_ORIGIN}${APP_BOUNCE_PATH}?to=${encodeURIComponent(linkUrl.href)}`;
}

/**
 * x-safari only. Facebook's prompt often stays open past the delay before the
 * next attempt, and a googlechromes attempt could take its place.
 */
export function buildAppBounceAttempts(pageUrl: string): EscapeAttempt[] {
  return [xSafariAttempt(buildAppBounceUrl(pageUrl))];
}

// Without the fragment, which the bounce drops on the way back.
function bouncedPageId(pageUrl: string): string {
  const { pathname, search } = new URL(pageUrl);
  return encodeURIComponent(pathname + search);
}

/**
 * Set before a tap bounces, so the link page it comes back to when the app is
 * missing opens the store instead of bouncing again.
 */
export function markBounced(
  pageUrl: string,
  pasteboardToken: string | null = null
): void {
  const pageId = bouncedPageId(pageUrl);
  markRedirectAttempted(BOUNCED_KEY, pageId);
  try {
    document.cookie = pasteboardToken
      ? buildRedirectAttemptCookie(
          BOUNCED_PASTEBOARD_TOKEN_KEY,
          `${pasteboardToken}:${pageId}`
        )
      : `${BOUNCED_PASTEBOARD_TOKEN_KEY}=; path=/; max-age=0`;
  } catch (e) {
    console.warn("Cookie write blocked:", e);
  }
}

/** markBounced for a response that sends the visitor to the bounce itself. */
export function buildBouncedCookie(pageUrl: string): string {
  return buildRedirectAttemptCookie(BOUNCED_KEY, bouncedPageId(pageUrl));
}

export function hasBounced(pageUrl: string): boolean {
  return hasRedirectBeenAttempted(BOUNCED_KEY, bouncedPageId(pageUrl));
}

/** hasBounced for the server, from the request's Cookie header. */
export function hasBouncedInCookieHeader(
  cookieHeader: string | null,
  pageUrl: string
): boolean {
  if (!cookieHeader) return false;
  return cookieHeader
    .split(/;\s*/)
    .includes(`${BOUNCED_KEY}=${bouncedPageId(pageUrl)}`);
}

export function readBouncedPasteboardToken(pageUrl: string): string | null {
  const prefix = `${BOUNCED_PASTEBOARD_TOKEN_KEY}=`;
  try {
    const [token, pageId] =
      document.cookie
        .split("; ")
        .find((cookie) => cookie.startsWith(prefix))
        ?.slice(prefix.length)
        .split(":") ?? [];
    if (pageId !== bouncedPageId(pageUrl)) return null;
    return PASTEBOARD_TOKEN_PATTERN.test(token) ? token : null;
  } catch (e) {
    console.warn("Cookie read blocked:", e);
    return null;
  }
}

/**
 * A script redirect, not an HTTP one: iOS skips Universal Links after an HTTP
 * redirect. The caller must only pass a link on a host we serve.
 */
export function buildAppBounceDocument(linkUrl: string): string {
  return buildSpinnerDocument({
    title: "Opening…",
    label: "Opening",
    body: `<a id="continue" href="${escapeHtml(linkUrl)}" hidden>Continue</a>
<script>
(function () {
  // When the app opens, it takes the navigation and this page stays behind
  // it. A visitor who comes back gets the link to tap instead of a spinner
  // that never ends. Not a second redirect: that would open the app again.
  function offerLink() {
    document.querySelector(".spinner").hidden = true;
    document.getElementById("continue").hidden = false;
  }
  document.addEventListener("visibilitychange", function () {
    if (document.visibilityState === "visible") offerLink();
  });
  window.addEventListener("pageshow", function (event) {
    if (event.persisted) offerLink();
  });
  window.location.replace(${toScriptJson(linkUrl)});
})();
</script>
<noscript><a href="${escapeHtml(linkUrl)}">Continue</a></noscript>`,
  });
}
